import styled from 'styled-components';
import {query, space} from '@theme/fn';
import Link from '@components/Link';
import {GetStaticProps} from 'next';

import {font} from '@design/templateFns';
import {RenderProseContent} from '@components/PortableText';

import {
  getLocationsPaths,
  getLocationDetailPageData,
} from '@data/locationsPage';
import getSiteConfig from '@data/siteConfig';
import Layout from '@components/Layout/Layout';
import Stretch from '@components/Layout/Stretch';
import Page from '@components/Page';
import AnnouncementBar from '@components/AnnouncementBarComponent/mapSiteConfig';
import Navigation from '@components/Navigation/mapSiteConfig';
import {Footer} from '@components/FooterComponent';
import {PageDocument, SiteConfig} from '@data/types';
import {Block} from '@components/Layout';

import {
  LocationDetailHero,
  LocationDetailHeroProps,
} from '@components/LocationDetailHero';

import {Breadcrumbs} from '@components/Breadcrumbs';
import {CircleArrow} from '@components/Arrow/index';
import {Location} from '@schema/types';
import {ResolvedSanityReferences} from '@data/types';

export async function getStaticPaths() {
  const paths = (await getLocationsPaths()).map((path) => ({
    params: {
      slug: path,
    },
  }));
  return {
    paths,
    fallback: 'blocking',
  };
}
// misc
import log from '@util/logging';
import ProseBlock from '@components/ProseBlock';

export interface slugPageContext {
  params: {
    // because this is an _optional_ catch-all page, next will usually set this
    // to an array of path segments. But for the root path, it isn't set at all.
    slug?: string;
  };
  preview?: boolean;
}

export interface slugPageProps {
  page: PageDocument;
  siteConfig: SiteConfig;
  preview?: boolean;
}

export const getStaticProps: GetStaticProps = async (
  context: slugPageContext,
) => {
  const {params, preview} = context;
  // if params.slug is missing this is the root, re-map to home
  const slug = params.slug;
  try {
    const pageData = await getLocationDetailPageData(slug, preview);

    if (!pageData) {
      return {notFound: true};
    }

    const siteConfig = await getSiteConfig();
    return {
      props: {
        page: pageData,
        siteConfig: siteConfig,
        preview: !!preview,
      } as slugPageProps,
    };
  } catch (e) {
    log.error(e);
    throw e; // best way to get 500 page?
  }
};

// Back Button
const MoreCTALink = styled.a`
  cursor: pointer;
  text-decoration: none;
  display: flex;
  color: rgba(0, 0, 0, 1);
  font-weight: 700;
  font-size: 14px;
  align-items: center;

  @media screen and (${query.atLeast('desktop')}) {
    font-size: 18px;
  }
`;

const CTAText = styled.div`
  ${font('headline-xs')};
  margin-left: ${space('sm')};
  margin-bottom: 5px;
`;

// Slugs to use
const locationSlug = '/company/locations';

const LocationDetailPage = (props) => {
  const {page} = props;

  // BREADCRUMB DATA
  const breadcrumbPages = [
    {
      title: 'Our Locations',
      slug: {current: locationSlug},
    },
    {
      title: page.title,
      slug: {current: page.slug?.current},
    },
  ];

  type LocationData = ResolvedSanityReferences<Location>;

  // MAPPED COMPONENTS
  const MappedLocationDetailHero = (block: LocationData) => {
    const props: LocationDetailHeroProps = {
      title: block.title,
      description: block.description ? (
        <RenderProseContent content={block.description} />
      ) : null,
      division: block.division.title,
      region: block.region.title,
      phone: block.phone,
      locationImage: block.image,
      physicalLocation: block.physicalLocation,
      addressLine1: block.addressLine1,
      addressLine2: block.addressLine2 ? block.addressLine2 : '',
      addressLine3: block.addressLine3 ? block.addressLine3 : '',
      addressLine4: block.addressLine4 ? block.addressLine4 : '',
    };

    return <LocationDetailHero {...props} />;
  };

  return (
    <Page
      {...{
        description: page.shortDescription,
        metatitle: page.metatitle,
        openGraphImage: page.mainImage,
      }}
    >
      <Layout>
        <AnnouncementBar {...(props.siteConfig as SiteConfig)} />
        <Navigation {...(props.siteConfig as SiteConfig)} />
        <Breadcrumbs pages={breadcrumbPages} />
        <MappedLocationDetailHero {...page} />
        <Block>
          <Link href={locationSlug}>
            <MoreCTALink>
              <CircleArrow flip /> <CTAText>Back to Our Locations</CTAText>
            </MoreCTALink>
          </Link>
        </Block>
        <Stretch />
        <Footer siteConfig={props.siteConfig as SiteConfig} />
      </Layout>
    </Page>
  );
};

export default LocationDetailPage;
